"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.LocationService = void 0;
const xss_1 = __importDefault(require("xss"));
// Global cache for client location
let cachedLocation = null;
class LocationService {
    isValidURL(url) {
        if (typeof url !== 'string')
            return false;
        try {
            const parsedURL = new URL(url);
            return parsedURL.hostname.endsWith('googleapis.com');
        }
        catch {
            return false;
        }
    }
    /**
     * Fetches address data from Google Maps API using a lat/lng JSON object.
     * Returns the raw address JSON or throws on error.
     */
    async fetchAddressFromLatLong(json) {
        if (!window.GOOGLE_MAPS_CLIENT_KEY)
            throw new Error('Google Maps client key undefined');
        if (!json ||
            typeof json !== 'object' ||
            !('location' in json) ||
            typeof json.location
                .lat !== 'number' ||
            typeof json.location
                .lng !== 'number') {
            throw new Error('The geolocation service failed');
        }
        const { lat, lng } = json
            .location;
        const url = new URL('https://maps.googleapis.com/maps/api/geocode/json');
        url.searchParams.append('latlng', (0, xss_1.default)(`${lat},${lng}`));
        url.searchParams.append('key', window.GOOGLE_MAPS_CLIENT_KEY);
        if (!this.isValidURL(url.toString())) {
            throw new Error('Invalid URL');
        }
        const res = await fetch(url.toString());
        if (!res.ok) {
            throw new Error('Service unreachable');
        }
        return res.json();
    }
    returnLocationFromAddressJson(json) {
        if (!json ||
            typeof json !== 'object' ||
            !('results' in json) ||
            !Array.isArray(json.results) ||
            !json.results[0] ||
            !Array.isArray(json.results[0].address_components)) {
            return {
                address: '',
                city: '',
                country: { abbr: '', name: '' },
                postalCode: '',
                stateProvince: { abbr: '', name: '' }
            };
        }
        const first = json.results[0].address_components;
        // Helper to extract a value from address_components by type
        const extractAddressField = (type, key = 'short_name') => {
            const comp = first.find((c) => Array.isArray(c.types) &&
                c.types.includes(type));
            return comp ? comp[key] : '';
        };
        const address = `${extractAddressField('street_number')} ${extractAddressField('route', 'long_name')}`.trim();
        const city = extractAddressField('locality');
        const postalCode = extractAddressField('postal_code');
        const abbr = extractAddressField('administrative_area_level_1');
        const name = extractAddressField('administrative_area_level_1', 'long_name');
        const cAbbr = extractAddressField('country');
        const cName = extractAddressField('country', 'long_name');
        const stateProvince = { abbr, name };
        const country = { abbr: cAbbr, name: cName };
        return { address, city, country, postalCode, stateProvince };
    }
    async get() {
        if (cachedLocation)
            return cachedLocation;
        if (!window.GOOGLE_MAPS_CLIENT_KEY)
            return {
                address: '',
                city: '',
                country: { abbr: '', name: '' },
                postalCode: '',
                stateProvince: { abbr: '', name: '' }
            };
        const GOOGLE_LOCATION_API = `https://www.googleapis.com/geolocation/v1/geolocate?key=${window.GOOGLE_MAPS_CLIENT_KEY}`;
        return fetch(GOOGLE_LOCATION_API, { method: 'POST' })
            .then(geolocRes => {
            if (!geolocRes.ok)
                return {
                    address: '',
                    city: '',
                    country: { abbr: '', name: '' },
                    postalCode: '',
                    stateProvince: { abbr: '', name: '' }
                };
            return geolocRes.json();
        })
            .then(geolocJson => this.fetchAddressFromLatLong(geolocJson))
            .then(addressJson => {
            const location = this.returnLocationFromAddressJson(addressJson);
            cachedLocation = location;
            return location;
        })
            .catch(() => {
            // Only log a generic error message to avoid leaking details
            console.error('Failed to get location');
            return {
                address: '',
                city: '',
                country: { abbr: '', name: '' },
                postalCode: '',
                stateProvince: { abbr: '', name: '' }
            };
        });
    }
    async getPostalCode() {
        const location = await this.get();
        return location?.postalCode || '';
    }
    clearCache() {
        cachedLocation = null;
    }
}
exports.LocationService = LocationService;
